import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { connect, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { browserHistory } from 'react-router';
import _isEqual from 'lodash/isEqual';
import { useEffectOnce } from '@zola/zola-ui/src/hooks/useEffectOnce';
import { CARD_TYPE_MAP } from '@zola/zola-ui/src/paper/cards/constants/cardTypes';
import { getCardPlpPath } from '@zola/zola-ui/src/paper/cards/util/getCardUrl';
import { PLP_MODULE_GROUP_TAGS } from '~/cards/constants/plpModuleGroupTags';
import { useSessionStorageData } from '~/cards/components/CardListing/PhotoPreview/hook';
import Breadcrumbs from '~/cards/components/CardListing/Breadcrumbs';
import { Hero } from '~/cards/components/CardListing/Hero';
import { FilterBubbles } from '~/cards/components/CardListing/FilterBubbles';
import { CardListingHeader } from '~/cards/components/CardListing/CardListingRedesign/CardListingHeader';
import CardListingFilter from '~/cards/components/CardListing/CardListingRedesign/CardListingFilter/CardListingFilter';
import PaperAddOnsFilters from '~/cards/components/CardListing/CardListingRedesign/PaperAddOnsFilters';
import PaginatedCardContainerOriginal from '~/cards/components/CardListing/PaginatedCardContainer/PaginatedCardContainerOriginal_next';
import { SUPPORTED_CARD_TYPES as supportedSamplesCardTypes } from '~/cards/components/common/Samples/constants';
import { getMultiSampleState } from '~/cards/selectors/samples';
import {
  getIsAdminView,
  getSearchFilters,
  getCardCatalogBusy,
  getDisplayableSearchTotal,
} from '~/cards/selectors/cardCatalogSelector';
import { getPLPPhotoPreviewState } from '~/cards/selectors/plpPhotoPreview';
import fetchModuleGroup from '~/actions/ModuleGroupActions';
import {
  updateSearchFilters,
  resetSearchFilters,
  updateMultipleSearchFilters,
  overwriteSearchFilters,
} from '~/cards/actions/cardCatalogActions';
import { getCart } from '~/actions/CartActions';
import { openModal, hideModal } from '~/cards/actions/modalActions';
import { getModuleGroup, getModules } from '~/selectors/moduleGroupSelectors';
import { getUserContext } from '~/selectors/user/userSelectors';
import { getHeroNamedStyle, existenceCheck, getNewPathNameAndSearch } from './utils';
import {
  DEFAULT_CARDS_PER_PAGE,
  DEFAULT_CARDS_PER_PAGE_MOBILE,
  SPECIAL_SIZE_CARDS_PER_PAGE,
} from './constants';

/**
 *  RecentlyViewed component use localStorage to save the data
 */
const DynamicRecentlyViewed = dynamic(() => import('~/cards/components/common/RecentlyViewed'), {
  ssr: false,
});

function getCurrentlyAppliedFilters(allFilters) {
  const appliedFilters = {};

  Object.keys(allFilters).forEach((key) => {
    if (existenceCheck(allFilters[key])) {
      appliedFilters[key] = allFilters[key];
    }
  });

  return appliedFilters;
}

const CardListingRedesign = ({
  activeCardEvent,
  adTileModules,
  saleTileModule,
  isAdminView,
  searchFilters,
  onUpdateSearchFilters,
  onUpdateMultipleSearchFilter,
  onOverwriteSearchFilters,
  onResetSearchFilters,
  moduleGroup,
  loadModuleGroup,
  tag,
  fetchCart,
  openCustomModal,
  hideCustomModal,
  userContext,
  activeStyles = [],
  cardTypeUrl,
  cardCatalogBusy,
  allMotifs,
  isMobile,
  isDesktop,
  trackPageView,
  multiSampleState,
  promoTag,
  seoData,
}) => {
  const [cardsPerPage, setCardsPerPage] = useState(
    isMobile ? DEFAULT_CARDS_PER_PAGE_MOBILE : DEFAULT_CARDS_PER_PAGE
  );

  const isPaperAddOns = activeCardEvent === CARD_TYPE_MAP.paperAddOns;

  const searchTotal = useSelector(getDisplayableSearchTotal);
  const [scrollActive, setScrollActive] = useState(false);
  const refTopOfPage = useRef();
  const ads = useMemo(() => (adTileModules?.length ? adTileModules : []), [adTileModules]);
  const { digital_suite: isDigital, lead_card_type: cardType } = searchFilters;

  const plpPhotoPreviewSessionStorageData = useSessionStorageData(cardType, isDigital);

  useEffect(() => {
    const isMatchingSuites = activeCardEvent === CARD_TYPE_MAP.matchingSuites;
    const updateCardsPerPage = () => {
      const isSpecialSize =
        window.innerWidth > 1599 && window.innerWidth < 1920 && !isMatchingSuites;
      if (isSpecialSize) {
        setCardsPerPage(SPECIAL_SIZE_CARDS_PER_PAGE);
      } else {
        setCardsPerPage(isMobile ? DEFAULT_CARDS_PER_PAGE_MOBILE : DEFAULT_CARDS_PER_PAGE);
      }
    };

    updateCardsPerPage();
    window.addEventListener('resize', updateCardsPerPage);

    return () => {
      window.removeEventListener('resize', updateCardsPerPage);
    };
  }, [activeCardEvent, isMobile]);

  useEffect(() => {
    if (!moduleGroup) {
      loadModuleGroup(tag);
    }
  }, [moduleGroup, loadModuleGroup, tag]);

  useEffect(() => {
    trackPageView();
  }, [
    searchFilters.has_foil,
    searchFilters.has_custom_photo,
    searchFilters.has_no_custom_photo,
    searchFilters.has_magnet,
    searchFilters.is_letterpress,
    searchFilters.is_portrait,
    searchFilters.is_landscape,
    searchFilters.is_square,
    searchFilters.is_vow_renewal,
    searchFilters.featured_artist,
    activeStyles,
    trackPageView,
  ]);

  useEffectOnce(() => {
    fetchCart();
  });

  useEffect(() => {
    // * External Simple Sign Up
    if (
      activeCardEvent === CARD_TYPE_MAP.holiday ||
      activeCardEvent === CARD_TYPE_MAP.weddingShower
    ) {
      const triggerSimpleSignupModal = () =>
        openCustomModal(
          'SIMPLE_SIGNUP_MODAL',
          {
            cardType: activeCardEvent,
            closeModal: hideCustomModal,
            flowEntry: 'CUSTOMIZATION_DRAFT',
            isGuest: userContext.is_guest,
            loginCallback: () => window.location.reload(),
            shouldShowPartnerField: false,
            signupLinkCallback: triggerSimpleSignupModal,
          },
          {
            size: 'xs',
            className: 'simple-signup-modal',
          }
        );
      window.zolaPaper.openSignUpModal = triggerSimpleSignupModal;
    }
  }, [activeCardEvent, hideCustomModal, openCustomModal, userContext]);

  const allowMultiSample = supportedSamplesCardTypes.includes(cardType) && !isDigital;

  const updateUrlWithFilters = useCallback(
    (newValues) => {
      const { pathname, search, query } = browserHistory.getCurrentLocation();

      const { newPathname, newSearch } = getNewPathNameAndSearch(
        newValues,
        cardTypeUrl,
        query.multi_sample_mode
      );

      //* UPDATE THE URL WITH NEW PATHNAME AND QUERY PARAMS
      // Only update the URL if it has changed
      if (newPathname !== pathname || newSearch !== search) {
        // Update the current URL
        browserHistory.push({
          pathname: newPathname,
          search: newSearch,
        });

        // Update the filters in Redux so the displayed cards refresh
        onOverwriteSearchFilters(newValues);
      }
    },
    [cardTypeUrl, onOverwriteSearchFilters]
  );

  const scrollToTop = useCallback(() => {
    if (refTopOfPage?.current && scrollActive) {
      window.scrollTo({
        top: refTopOfPage.current.offsetTop,
        left: 0,
      });
    }
  }, [scrollActive]);

  const handleFilterUpdate = useCallback(
    (key, value) => {
      // If the page is loading or the specified filter is unchanged, do nothing.
      if (cardCatalogBusy || _isEqual(searchFilters[key], value)) {
        return;
      }

      scrollToTop();
      onUpdateSearchFilters(key, value);
      updateUrlWithFilters({
        ...getCurrentlyAppliedFilters(searchFilters),
        [key]: value,
      });
    },
    [cardCatalogBusy, searchFilters, scrollToTop, onUpdateSearchFilters, updateUrlWithFilters]
  );

  function handleMultipleFiltersUpdate(newValues) {
    const anyFiltersHaveChanges = Object.keys(newValues).some(
      (key) => !_isEqual(searchFilters[key], newValues[key])
    );

    // If the page is loading or the specified filter is unchanged, do nothing.
    if (cardCatalogBusy || !anyFiltersHaveChanges) {
      return;
    }

    scrollToTop();
    onUpdateMultipleSearchFilter(newValues);
    updateUrlWithFilters({
      ...getCurrentlyAppliedFilters(searchFilters),
      ...newValues,
    });
  }

  function clearFilters() {
    if (cardCatalogBusy) return;

    onResetSearchFilters();
    browserHistory.push(getCardPlpPath(activeCardEvent, isAdminView, isDigital));
  }

  const plpPhotoPreviewState = useSelector(getPLPPhotoPreviewState);
  const { currentUploadcareFile } = useSessionStorageData(cardType, isDigital);
  const hasUploadcareFile = !!(plpPhotoPreviewState?.uploadcareFile || currentUploadcareFile);
  const [filterInitialized, setFilterInitialized] = useState(false);
  useEffect(() => {
    if (hasUploadcareFile && !filterInitialized && !cardCatalogBusy) {
      setFilterInitialized(true);
      handleFilterUpdate('has_custom_photo', true);
    }
  }, [cardCatalogBusy, filterInitialized, hasUploadcareFile, handleFilterUpdate]);

  const currentCardType = isDigital ? CARD_TYPE_MAP.dstd : activeCardEvent;
  const currentStyleName = getHeroNamedStyle(activeStyles);

  return (
    <div id="card-listing" className="card-listing" ref={refTopOfPage}>
      <CardListingHeader id="card-listing-header">
        <Breadcrumbs currentCardType={currentCardType} />
        <Hero activeCardEvent={currentCardType} seoData={seoData} />
        <FilterBubbles
          activeCardEvent={currentCardType}
          overwriteFilters={handleMultipleFiltersUpdate}
        />
        {isPaperAddOns ? (
          <PaperAddOnsFilters />
        ) : (
          <CardListingFilter
            onUpdateSearchFilters={handleFilterUpdate}
            searchFilters={searchFilters}
            handleFilterUpdate={handleFilterUpdate}
            scrollActive={scrollActive}
            setScrollActive={setScrollActive}
            activeCardEvent={activeCardEvent}
            allowMultiSample={allowMultiSample}
            clearFilters={clearFilters}
            scrollToTop={scrollToTop}
            isMobile={isMobile}
            isDesktop={isDesktop}
            allMotifs={allMotifs}
            userContext={userContext}
            multiSampleState={multiSampleState}
            searchTotal={searchTotal}
          />
        )}
      </CardListingHeader>
      <div className="plpRedesignCardWrapper">
        <PaginatedCardContainerOriginal
          cardsPerPage={cardsPerPage}
          handleFilterUpdate={handleFilterUpdate}
          ads={ads}
          saleTile={saleTileModule}
          promoTag={promoTag}
          activeCardEvent={activeCardEvent}
          isAdminView={isAdminView}
          searchFilters={searchFilters}
          currentStyleName={currentStyleName}
          allowMultiSample={allowMultiSample}
          cardType={cardType}
          clearFilters={clearFilters}
          isMobile={isMobile}
          isDesktop={isDesktop}
          isDigital={isDigital}
          scrollToTop={scrollToTop}
          multiSampleEnabled={multiSampleState?.enabled}
          plpPhotoPreviewData={plpPhotoPreviewSessionStorageData}
          userContext={userContext}
        />
      </div>
      {!isDigital && <DynamicRecentlyViewed />}
    </div>
  );
};

CardListingRedesign.propTypes = {
  isAdminView: PropTypes.bool,
  activeCardEvent: PropTypes.string,
  searchFilters: PropTypes.shape({
    lead_card_type: PropTypes.string,
    multiColor: PropTypes.array,
    motifs: PropTypes.array,
    silhouettes: PropTypes.array,
    has_foil: PropTypes.bool,
    has_custom_photo: PropTypes.bool,
    has_no_custom_photo: PropTypes.bool,
    has_digital: PropTypes.bool,
    has_magnet: PropTypes.bool,
    is_letterpress: PropTypes.bool,
    is_portrait: PropTypes.bool,
    is_landscape: PropTypes.bool,
    is_square: PropTypes.bool,
    is_vow_renewal: PropTypes.bool,
    featured_artist: PropTypes.bool,
  }),
  onUpdateSearchFilters: PropTypes.func.isRequired,
  onResetSearchFilters: PropTypes.func.isRequired,
  adTileModules: PropTypes.arrayOf(PropTypes.shape({})),
  moduleGroup: PropTypes.shape({}),
  loadModuleGroup: PropTypes.func,
  tag: PropTypes.string,
};

const isAdTileModule = (module) => module.title && module.title.startsWith('PLPTileAd -');
const isSaleTileModule = (module) => module.title && module.title.startsWith('PLPSaleTileAd -');

const mapStateToProps = (state, ownProps) => {
  const activeCardType = ownProps.isDigital
    ? `DIGITAL_${ownProps.activeCardEvent}`
    : ownProps.activeCardEvent;
  const tag = PLP_MODULE_GROUP_TAGS[activeCardType];

  return {
    isAdminView: getIsAdminView(state),
    moduleGroup: getModuleGroup(state, { tag }),
    adTileModules: getModules(state, { tag, searchFn: isAdTileModule }),
    saleTileModule: getModules(state, { tag, searchFn: isSaleTileModule }),
    searchFilters: getSearchFilters(state),
    tag,
    userContext: getUserContext(state),
    cardCatalogBusy: getCardCatalogBusy(state),
    multiSampleState: getMultiSampleState(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  onUpdateSearchFilters: (key, value) => dispatch(updateSearchFilters(key, value)),
  onUpdateMultipleSearchFilter: (newValues) => dispatch(updateMultipleSearchFilters(newValues)),
  onOverwriteSearchFilters: (newValues) => dispatch(overwriteSearchFilters(newValues)),
  onResetSearchFilters: () => dispatch(resetSearchFilters()),
  loadModuleGroup: (tag) => dispatch(fetchModuleGroup(tag)),
  fetchCart: () => dispatch(getCart()),
  openCustomModal: (modal, props, options) => dispatch(openModal(modal, props, options)),
  hideCustomModal: () => dispatch(hideModal()),
});

export default connect(mapStateToProps, mapDispatchToProps)(CardListingRedesign);
